import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.jsx';
import DefaultWebLayout from './layouts/DefaultWebLayout.jsx';
import RoleRoute from './components/RoleRoute.jsx';
import './App.scss';

// Public marketing chrome (sticky header + rich footer) for the landing page.
const PublicLayout = lazy(() => import('./layouts/PublicLayout.jsx'));

// Public marketing landing + top-of-funnel readiness calculators (anonymous).
const HomePage = lazy(() => import('./pages/home/index.jsx'));

// Public authentication pages (shared AuthForm — login/register modes).
const LoginPage = lazy(() =>
  import('./pages/login/index.jsx').then((m) => ({ default: m.LoginPage }))
);
const RegisterPage = lazy(() =>
  import('./pages/login/index.jsx').then((m) => ({ default: m.RegisterPage }))
);

// Themed status/error pages (created by create-frontend-layout-components).
const Unauthorized401 = lazy(() =>
  import('./pages/status/index.jsx').then((m) => ({ default: m.Unauthorized401 }))
);
const Forbidden403 = lazy(() =>
  import('./pages/status/index.jsx').then((m) => ({ default: m.Forbidden403 }))
);
const NotFound404 = lazy(() =>
  import('./pages/status/index.jsx').then((m) => ({ default: m.NotFound404 }))
);
const ServerError500 = lazy(() =>
  import('./pages/status/index.jsx').then((m) => ({ default: m.ServerError500 }))
);

// Role-scoped pages.
const SiteRegistryPage = lazy(() => import('./pages/site-registry/index.jsx'));
const WorkflowTrackerPage = lazy(() => import('./pages/workflow-tracker/index.jsx'));
const EvidenceAssemblerPage = lazy(() => import('./pages/evidence-assembler/index.jsx'));
const TelemetryGoLivePage = lazy(() => import('./pages/telemetry-go-live/index.jsx'));
const GraphIqBrainPage = lazy(() => import('./pages/graphiq-brain/index.jsx'));
const RecLedgerPage = lazy(() => import('./pages/rec-ledger/index.jsx'));
const ProsumerPoolingPage = lazy(() => import('./pages/prosumer-pooling/index.jsx'));
const MonetizationPage = lazy(() => import('./pages/monetization/index.jsx'));
const MrvEmissionsPage = lazy(() => import('./pages/mrv-emissions/index.jsx'));
const VerifierSpacePage = lazy(() => import('./pages/verifier-space/index.jsx'));
const UtilityRpsPage = lazy(() => import('./pages/utility-rps/index.jsx'));

// Operator/admin administration — landing dashboard + user management.
const AdministrationPage = lazy(() => import('./pages/administration/index.jsx'));
const UserManagementPage = lazy(() => import('./pages/user-management/index.jsx'));

// Page routes are added here by ui-components-from-reference as each page is
// generated (keyed on its menuData slug). Until a page exists, its slug has no
// route and the `*` catch-all renders the themed 404 — the nav never dead-ends.

const Loader = () => (
  <div className="d-flex justify-content-center py-5" style={{ paddingTop: '80px' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Standalone auth screens — rendered WITHOUT the app chrome
                (no navbar/footer) so the OS-series split panel is full-bleed. */}
            <Route path="login" element={<LoginPage />} />
            <Route path="register" element={<RegisterPage />} />

            {/* Public marketing landing — rendered in its own PublicLayout
                (marketing header + rich footer), separate from the app chrome. */}
            <Route element={<PublicLayout />}>
              <Route index element={<HomePage />} />
              <Route path="home" element={<HomePage />} />
            </Route>

            {/* Shared layout (header/nav/footer/account chrome) wraps every page */}
            <Route element={<DefaultWebLayout />}>
              {/* Operator/admin administration — landing dashboard + user mgmt. */}
              <Route element={<RoleRoute allow={['operator', 'admin']} />}>
                <Route path="administration" element={<AdministrationPage />} />
                <Route path="user-management" element={<UserManagementPage />} />
              </Route>

              {/* Role-scoped page routes — each wrapped in a RoleRoute group,
                  registered AFTER the public routes above. */}
              <Route element={<RoleRoute allow={['epc_installer', 'operator', 'admin']} />}>
                <Route path="site-registry" element={<SiteRegistryPage />} />
                <Route path="workflow-tracker" element={<WorkflowTrackerPage />} />
                <Route path="evidence-assembler" element={<EvidenceAssemblerPage />} />
                <Route path="telemetry-go-live" element={<TelemetryGoLivePage />} />
              </Route>

              {/* Operator-only compliance workspace */}
              <Route element={<RoleRoute allow={['operator', 'admin']} />}>
                <Route path="graphiq-brain" element={<GraphIqBrainPage />} />
                <Route path="rec-ledger" element={<RecLedgerPage />} />
                <Route path="monetization" element={<MonetizationPage />} />
              </Route>

              {/* Prosumer/Corporate self-service portal */}
              <Route element={<RoleRoute allow={['prosumer', 'admin']} />}>
                <Route path="prosumer-pooling" element={<ProsumerPoolingPage />} />
              </Route>

              {/* Corporate Sustainability Lead — MRV compliance */}
              <Route element={<RoleRoute allow={['sustainability_lead', 'admin']} />}>
                <Route path="mrv-emissions" element={<MrvEmissionsPage />} />
                <Route path="verifier-space" element={<VerifierSpacePage />} />
              </Route>

              {/* Utility / DU — RPS compliance */}
              <Route element={<RoleRoute allow={['utility', 'admin']} />}>
                <Route path="utility-rps" element={<UtilityRpsPage />} />
              </Route>

              {/* Status pages */}
              <Route path="401" element={<Unauthorized401 />} />
              <Route path="403" element={<Forbidden403 />} />
              <Route path="500" element={<ServerError500 />} />

              {/* Router catch-all — any unresolved path (including the default
                  landing until Home is built) lands on the themed 404. */}
              <Route path="*" element={<NotFound404 />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
